import { useState, useEffect } from 'react';
import { useMutation, useInfiniteQuery } from '@tanstack/react-query';
import type { BaseOptionType } from 'antd/lib/select';
import type { SelectProps } from './ui';
import { Select, Spin } from './ui';
import type { DataTestId } from '../types';

type InfiniteSelectProps<
  TItem,
  TValue,
  TOption extends BaseOptionType = BaseOptionType,
> = SelectProps<TValue, TOption> & {
  toOption: (item: TItem) => TOption;
  getAll: () => Promise<{ nextPage?: number; items: TItem[] }>;
  getOne: (id: TValue) => Promise<TItem>;
  queryKey: string;
} & DataTestId;

const LOADING_LABEL = 'Loading...';

export function InfiniteSelect<TItem, TValue, TOption extends BaseOptionType>({
  getOne,
  getAll,
  toOption,
  queryKey,
  ...props
}: InfiniteSelectProps<TItem, TValue, TOption>) {
  const [options, setOptions] = useState([
    { label: LOADING_LABEL, value: props.value } as unknown as TOption,
  ]);
  const [enabled, setEnabled] = useState(false);

  const { mutate, isLoading: isLoadingOne } = useMutation((id: TValue) => getOne(id), {
    onSuccess(data) {
      setOptions([toOption(data)]);
    },
  });

  const { data, isInitialLoading } = useInfiniteQuery([queryKey], () => getAll(), {
    enabled,
    getNextPageParam: lastPage => lastPage.nextPage,
  });

  useEffect(() => {
    if (
      props.value &&
      !options.some(option => option.value === props.value && option.label !== LOADING_LABEL)
    ) {
      mutate(props.value);
    }
  }, [props.value]);

  useEffect(() => {
    if (!data) return;
    setOptions(data.pages.flatMap(page => page.items.map(doc => toOption(doc))));
  }, [data]);

  return (
    <Select
      className="w-100"
      options={options}
      notFoundContent={isInitialLoading ? <Spin position="center" /> : null}
      onDropdownVisibleChange={setEnabled}
      loading={isLoadingOne || isInitialLoading}
      {...props}
    />
  );
}
